import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Stack,
  alpha,
} from "@mui/material";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import { motion } from "framer-motion";

/* ------------------------------------------------------------------
 * StageLink brand tokens
 * ----------------------------------------------------------------*/
const GRADIENT = "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)";
const GRADIENT_START = "#6366F1"; // first stop of gradient for icon / border tints

const BusinessChoiceLanding: React.FC = () => {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

  const heroTint = alpha(GRADIENT_START, 0.15);
  const hoverTint = alpha(GRADIENT_START, 0.08);

  /* Gradient button styles */
  const gradientBtnSX = {
    fontFamily: '"Lexend", sans-serif',
    background: GRADIENT,
    color: "#fff",
    fontWeight: 600,
    textTransform: "none" as const,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.4, 3),
    "&:hover": {
      background: GRADIENT,
      boxShadow: "0 4px 14px rgba(99, 102, 241, 0.3)",
    },
  };

  return (
    <Box sx={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box component="header" sx={{ py: 2, px: 3 }}>
        <Typography
          variant="h5"
          fontFamily="Lexend, sans-serif"
          fontWeight={700}
          sx={{
            letterSpacing: 0.5,
            background: GRADIENT,
            WebkitBackgroundClip: "text",
            color: "transparent",
            whiteSpace: "nowrap",
          }}
        >
          TAASNet
        </Typography>
      </Box>

      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* tinted backdrop */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(circle at top left, ${heroTint}, transparent 70%)`,
            pointerEvents: "none",
          }}
        />

        <Container>
          <Grid container spacing={isSmUp ? 8 : 4} justifyContent="center">
            <Grid item xs={12} textAlign="center">
              <Typography
                component="h1"
                sx={{
                  fontFamily: "Lexend, sans-serif",
                  fontWeight: 800,
                  lineHeight: 1.1,
                  mb: 1,
                  background: GRADIENT,
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  fontSize: {
                    xs: "clamp(2.2rem, 7vw, 3rem)",
                    sm: "clamp(2.5rem, 5vw, 3.5rem)",
                  },
                }}
              >
                Where Talents & Brands Connect
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                fontFamily="Lexend, sans-serif"
              >
                Choose the account type that best fits your journey.
              </Typography>
            </Grid>

            {/* Individual Card */}
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              sx={{ display: "flex", borderRadius: 4 }}
            >
              <motion.div
                whileHover={{
                  y: -6,
                  scale: 1.02,
                  boxShadow: "0 8px 20px rgba(0, 0, 0, 0.12)",
                }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                style={{
                  flex: 1,
                  display: "flex",
                  overflow: "hidden",
                  borderRadius: "16px",
                  border: `1px solid ${alpha(GRADIENT_START, 0.3)}`,
                }}
              >
                <Box
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    backdropFilter: "blur(4px)",
                    textAlign: "center",
                    transition: "background 0.25s",
                    background: "rgba(255,255,255,0.85)", // Ensures background is always present
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      backgroundColor: alpha(GRADIENT_START, 0.04), // subtle change
                      // borderRadius: 4,
                      // border: `1px solid ${alpha(GRADIENT_START, 0.3)}`,
                    },
                  }}
                >
                  <Stack alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <PersonOutlineRoundedIcon
                      sx={{ fontSize: 48, color: GRADIENT_START }}
                    />
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      fontFamily="Lexend, sans-serif"
                    >
                      Individual Account
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                    fontFamily="Lexend, sans-serif"
                  >
                    Ideal for talents, creators, or everyday users who want to
                    book or offer services.
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/register"
                    size="large"
                    fullWidth
                    sx={gradientBtnSX}
                  >
                    Register as Individual
                  </Button>
                </Box>
              </motion.div>
            </Grid>

            {/* Business Card */}
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              sx={{ display: "flex", borderRadius: 4 }}
            >
              <motion.div
                whileHover={{
                  y: -6,
                  scale: 1.02,
                  boxShadow: "0 8px 20px rgba(0, 0, 0, 0.12)",
                }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                style={{
                  flex: 1,
                  display: "flex",
                  overflow: "hidden",
                  borderRadius: "16px ",
                  border: `1px solid ${alpha(GRADIENT_START, 0.3)}`, // <-- Move border here!
                }}
              >
                <Box
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    backdropFilter: "blur(4px)",
                    textAlign: "center",
                    transition: "background 0.25s",
                    background: "rgba(255,255,255,0.85)", // Ensures background is always present
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      backgroundColor: alpha(GRADIENT_START, 0.04), // subtle change
                      // borderRadius: 4,
                      // border: `1px solid ${alpha(GRADIENT_START, 0.3)}`,
                    },
                  }}
                >
                  <Stack alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <BusinessCenterRoundedIcon
                      sx={{ fontSize: 48, color: GRADIENT_START }}
                    />
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      fontFamily="Lexend, sans-serif"
                    >
                      Business Account
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                    fontFamily="Lexend, sans-serif"
                  >
                    Perfect for brands, venues, agencies, or institutions that
                    manage multiple talent profiles.
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/register/business"
                    size="large"
                    variant="outlined"
                    fullWidth
                    sx={{
                      ...gradientBtnSX,
                      background: "transparent",
                      color: GRADIENT_START,
                      border: `2px solid ${GRADIENT_START}`,
                      "&:hover": {
                        background: GRADIENT,
                        color: "#fff",
                        boxShadow: "0 4px 14px rgba(99, 102, 241, 0.3)",
                      },
                    }}
                  >
                    Register as Business
                  </Button>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ py: 2, textAlign: "center" }}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontFamily="Lexend, sans-serif"
        >
          © {new Date().getFullYear()} StageLink. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default BusinessChoiceLanding;